import React, { useEffect, useState } from 'react';
import { parseBRMoney, formatBRNumber } from '../lib/format';

/**
 * Currency / numeric input that accepts Brazilian formatting.
 *
 * - Display follows BR style: "1.500,03".
 * - Accepts both "," and "." as decimal separators while typing.
 * - On blur, the value is re-formatted to BR style for clarity.
 * - The `onChange` callback receives the parsed numeric value (Number).
 *
 * Props mirror a regular <input/>. Use `value` (Number) and `onChange(num)`.
 */
export default function MoneyInput({
  value,
  onChange,
  className = 'input-md',
  placeholder = '0,00',
  decimals = 2,
  allowNegative = false,
  ...rest
}) {
  // Internal display string — uncoupled from the numeric prop while typing.
  const [display, setDisplay] = useState(() =>
    value === 0 || value === '' || value == null ? '' : formatBRNumber(value, { decimals })
  );

  // Sync display when the numeric prop changes externally (e.g., form reset).
  useEffect(() => {
    const parsed = parseBRMoney(display);
    if (Math.abs(parsed - Number(value || 0)) > 0.0001) {
      setDisplay(value === 0 || value === '' || value == null ? '' : formatBRNumber(value, { decimals }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = (e) => {
    const raw = e.target.value;
    // Allow digits, comma, period, and optional leading "-" if allowed.
    const re = allowNegative ? /^-?[\d.,]*$/ : /^[\d.,]*$/;
    if (raw !== '' && !re.test(raw)) return;
    setDisplay(raw);
    onChange(parseBRMoney(raw));
  };

  const handleBlur = () => {
    const n = parseBRMoney(display);
    setDisplay(n ? formatBRNumber(n, { decimals }) : '');
    onChange(n);
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      value={display}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      className={className}
      {...rest}
    />
  );
}
